import React, { useMemo, useState } from "react";
import Modal from "../Modal/Modal";
import TreatmentPicker from "../TreatmentPicker/TreatmentPicker";
import PatientPicker from "../PatientPicker/PatientPicker";
import DentistPicker from "../DentistPicker/DentistPicker";
import { leaveOn, useClinicData } from "../../context/ClinicDataContext";
import { dayName, formatDateShort, formatTime, minutesToTime, timeToMinutes } from "../../utils/format";

// Books a new appointment, or amends an existing one when `appointment` is passed.
const NewAppointmentModal = ({ onClose, onSaved, defaults = {}, appointment }) => {
  const {
    patients,
    activeDentists,
    treatments,
    clinic,
    today,
    treatmentsDuration,
    dentistById,
    findClash,
    addAppointment,
    updateAppointment,
  } = useClinicData();
  const isEdit = Boolean(appointment);
  const source = appointment || defaults;

  const [form, setForm] = useState({
    patientId: source.patientId || "",
    dentistId: source.dentistId || activeDentists[0]?.id || "",
    treatmentIds: source.treatmentIds || [],
    date: source.date || today,
    start: source.start || "",
    notes: source.notes || "",
  });
  const [error, setError] = useState("");

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError("");
  };

  const duration = treatmentsDuration(form.treatmentIds);

  // Every slot in opening hours; slots where the visit would run past closing are disabled.
  const timeOptions = useMemo(() => {
    const open = timeToMinutes(clinic.openTime);
    const close = timeToMinutes(clinic.closeTime);
    const options = [];
    for (let m = open; m < close; m += clinic.slotMinutes) {
      options.push({ value: minutesToTime(m), tooLate: m + duration > close });
    }
    return options;
  }, [clinic, duration]);
  const startTooLate = timeOptions.find((t) => t.value === form.start)?.tooLate;

  const day = form.date ? dayName(form.date) : "";
  const dentist = dentistById[form.dentistId];
  const clinicClosed = day && !clinic.openDays.includes(day);
  const dentistOff = day && dentist && !dentist.days.includes(day);
  const dentistLeave = form.date && dentist ? leaveOn(dentist, form.date) : null;
  // Outside the dentist's own hours, or overlapping their break (a warning, not a block).
  const outsideHours = (() => {
    if (!dentist || !form.start) return "";
    const s = timeToMinutes(form.start);
    const e = s + duration;
    if (s < timeToMinutes(dentist.start) || e > timeToMinutes(dentist.end)) {
      return `${dentist.name} works ${formatTime(dentist.start)} – ${formatTime(dentist.end)}.`;
    }
    if (dentist.breakStart && s < timeToMinutes(dentist.breakEnd) && e > timeToMinutes(dentist.breakStart)) {
      return `This overlaps ${dentist.name}'s break (${formatTime(dentist.breakStart)} – ${formatTime(dentist.breakEnd)}).`;
    }
    return "";
  })();
  // Inactive dentists can't take new bookings, but an existing appointment keeps showing its dentist.
  const pickerDentists =
    dentist && !dentist.active ? [...activeDentists, dentist] : activeDentists;

  const moved =
    isEdit &&
    (form.date !== appointment.date || form.start !== appointment.start || form.dentistId !== appointment.dentistId);

  const handleSave = () => {
    if (!form.patientId || !form.dentistId || !form.treatmentIds.length || !form.date || !form.start) {
      setError("Choose a patient, a dentist, at least one treatment, a date and a start time.");
      return;
    }
    if (startTooLate) {
      setError(`This visit takes ${duration} min and would run past closing at ${formatTime(clinic.closeTime)}. Pick an earlier start time.`);
      return;
    }
    if (clinicClosed) {
      setError(`The clinic is closed on ${day}. Pick another date.`);
      return;
    }
    if (dentistLeave) {
      setError(`${dentist.name} is on ${dentistLeave.type.toLowerCase()} on ${formatDateShort(form.date)}. Pick another date or dentist.`);
      return;
    }
    const clash = findClash({ ...form, ignoreId: appointment?.id });
    if (clash) {
      setError(`${dentist.name} already has an appointment at ${formatTime(clash.start)} that overlaps this slot.`);
      return;
    }
    if (isEdit) {
      const changes = { ...form };
      if (moved) {
        // A new date or time needs the patient to confirm again; keep where it was moved from.
        changes.rescheduledFrom = { date: appointment.date, start: appointment.start, dentistId: appointment.dentistId };
        if (appointment.status === "confirmed") changes.status = "scheduled";
      }
      updateAppointment(appointment.id, changes);
      onSaved?.({ ...appointment, ...changes });
    } else {
      const appt = addAppointment(form);
      onSaved?.(appt);
    }
    onClose();
  };


  return (
    <Modal
      title={isEdit ? "Edit appointment" : "New appointment"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" type="button" onClick={handleSave}>
            {isEdit ? "Save changes" : "Book appointment"}
          </button>
        </>
      }
    >
      {moved && (
        <p className="appt-form__notice" role="status">
          Moving from {formatDateShort(appointment.date)}, {formatTime(appointment.start)}
          {form.dentistId !== appointment.dentistId ? ` with ${dentistById[appointment.dentistId]?.name}` : ""}.
          {appointment.status === "confirmed" ? " It will need confirming with the patient again." : ""}
        </p>
      )}
      <div className="form-grid">
        <div className="form-grid__full">
          <PatientPicker
            patients={patients}
            value={form.patientId}
            onChange={(id) => {
              setForm((f) => ({ ...f, patientId: id }));
              setError("");
            }}
            label="Patient"
            placeholder="Search name, phone or IC no."
          />
        </div>

        <div className="form-grid__full">
          <DentistPicker
            dentists={pickerDentists}
            value={form.dentistId}
            onChange={(id) => {
              setForm((f) => ({ ...f, dentistId: id }));
              setError("");
            }}
          />
        </div>

        <div className="form-grid__full">
          <TreatmentPicker
            treatments={treatments}
            value={form.treatmentIds}
            onChange={(ids) => {
              setForm((f) => ({ ...f, treatmentIds: ids }));
              setError("");
            }}
          />
        </div>

        <label className="form-field">
          <span className="form-field__label">Date</span>
          <input className="form-field__control" type="date" value={form.date} onChange={update("date")} />
          {dentistLeave ? (
            <span className="form-field__hint form-field__hint--warning">
              {dentist.name} is on {dentistLeave.type.toLowerCase()} this day.
            </span>
          ) : (
            dentistOff &&
            !clinicClosed && <span className="form-field__hint">{dentist.name} doesn't usually work on {day}.</span>
          )}
        </label>

        <label className="form-field">
          <span className="form-field__label">Start time ({duration} min visit)</span>
          <select className="form-field__control" value={form.start} onChange={update("start")}>
            <option value="">Select a time</option>
            {timeOptions.map((t) => (
              <option key={t.value} value={t.value} disabled={t.tooLate && t.value !== form.start}>
                {formatTime(t.value)}
                {t.tooLate ? " (runs past closing)" : ""}
              </option>
            ))}
          </select>
          {outsideHours && <span className="form-field__hint form-field__hint--warning">{outsideHours}</span>}
        </label>

        <label className="form-field form-grid__full">
          <span className="form-field__label">Notes (optional)</span>
          <textarea
            className="form-field__control"
            value={form.notes}
            onChange={update("notes")}
            placeholder="e.g. Tooth 36, sensitive to cold"
          />
        </label>
      </div>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default NewAppointmentModal;
